import { Box, Divider, Grid, Switch, Typography } from '@mui/material';
import PageHeader from 'components/Header/Page';
import React, { useEffect, useState } from 'react';
import SectorFilter from 'components/Dropdown/SectorFilter';
import LoaderComponent from 'components/Loader';
import Insights from './Insights';
import CalenderIcon from '../../assets/mdi_calendar.svg';
import {
    addOrUpdateInsights, deleteInsights, getDataEstateSector, getInsights,
    fetchIncidentsSummary, fetchMimsMetricTrend, fetchProblemTicketsMetricTrend,
    fetchHappySignalOverview, fetchHappySignalOverviewTrend
} from 'service/service';
import moment from 'moment';
import PageWrapper from 'components/Common/PageWraper';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import PieInfoWidget from 'components/Widgets/PieInfoWidget';
import dayjs from 'dayjs';
import MultiLineWidget from 'components/Widgets/MultiLineChart';
import IncidentFilter from 'components/Dropdown/IncidentFilter';
import ITSMOpsTrend from './ITSMOpsTrend';
import NumberWidget from 'components/Widgets/NumberWidget';
import ChartWidget from 'components/Widgets/ChartWidget';
import CustomBarChartWidget from 'components/Widgets/CustomBarChart';
import { useTheme } from "@mui/material/styles";
import YearMonthPicker from 'components/Dropdown/YearMonthPicker';
import { SECTOR_OPTIONS } from 'utils/xopsUtil';

const SectorMetrics = () => {
    const theme = useTheme();
    const [sectorName, setSectorName] = useState(() => {
        const storedValue = localStorage.getItem("xopsMIMReportsSector");
        return storedValue === null || storedValue === undefined ? "all" : storedValue;
    });
    const isXOps = true;
    const getInitialMonth = () => {
        return isXOps ? moment().subtract(1, 'month').format('YYYY-MM') : '0M';
    };
    const [customMonth, setCustomMonth] = useState(getInitialMonth());
    const [filterMonth, setFilterMonth] = useState(getInitialMonth());

    const [insights, setInsights] = useState<Insights[]>([]);
    const [loader, setLoader] = useState<boolean>(false);
    const [sectorOptionsList, setSectorOptionsList] = useState<any[]>(SECTOR_OPTIONS);
    const [chartLoading, setChartLoading] = useState(true);
    const [problemMetricChartLoader, SetProblemMetricChartLoader] = useState<boolean>(true);
    const [problemMetricChartOptions, SetProblemMetricChartOptions] = useState<Record<string, any>>({});
    const [widgetData, setWidgetData] = useState<any[]>([]);
    const [majorProblemBacklogData, setMajorProblemBacklogData] = useState<PieChartItem[]>([]);
    const [mimsMetricChartOptions, SetMimsMetricChartOptions] = useState<Record<string, any>>({});
    const [mimsRawData, setMimsRawData] = useState<{ currentYearIncident: any[], lastYearIncident: any[] }>({
        currentYearIncident: [],
        lastYearIncident: []
    });
    const [chartPreference, setChartPreference] = useState('all');
    const [mimsMetricChartLoader, SetMimsMetricChartLoader] = useState<boolean>(true);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isTrendLoading, setIsTrendLoading] = React.useState(false);
    const [responseVolumeData, setResponseVolumeData] = useState<any>(null);
    const [responseVolumeBarChartOptions, setResponseVolumeBarChartOptions] = useState<any>({});
    const [happinessIndexTrendData, setHappinessIndexTrendData] = useState<{ [key: string]: number } | null>({});
    const [happysignalsData, setHappysignalsData] = useState({
        happinessIndex: { value: 0, change: '-' },
        averageLostTime: { value: '', change: '-' },
        responseRate: { value: '', change: '-' },
        uniquePersonas: { value: 0, change: '-' }
    });
    const [lastYearAvgData, setLastYearAvgData] = useState<[string, number][]>([]);
    const [isClosed, setIsClosed] = useState(true);

    const mimColorMapping = {
        'MIMs': '#299BFF',
        'Preventive MIMs': '#01C008'
    };
    const majorProblemBacklogColorMapping = {
        'Root Cause Analysis': '#299BFF',
        'Fix In Progress': '#01C008',
        'Closed': '#FFA200'
    };

    interface PieChartItem {
        name: string;
        value: number;
        formattedName: string;
    }

    interface Insights {
        insightId: number;
        title: string;
        description: string;
        opsType: string;
        createdBy: string;
        updatedBy: string;
        createdDate: string;
        updatedDate: string;
    }

    //Function to fetch insights
    const fetchInsights = async (selectedMonth: string) => {
        setLoader(true);
        try {
            const response = await getInsights('Sector Metrics', '', selectedMonth);
            if (response.success) {
                setInsights(response.data.data);
                setLoader(false);
                console.log('response', response);
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Failed to get insights:', error);
        }
    };

    async function fetchSectorOptionsData() {
        try {
            const sectorKpiRes = await getDataEstateSector();
            if (sectorKpiRes.success) {
                if (sectorKpiRes.data) {
                    const sectorValues = sectorKpiRes.data.map((item: { sector: any }) => item.sector);

                    // --- MODIFICATION START: Filter out AMESA/EUROPE and add EMEA ---
                    // 1. Create a set to handle duplicates and easy lookups
                    const uniqueSectorsSet = new Set(sectorValues);

                    // 2. Check if AMESA or EUROPE exist (case insensitive check is safer if data varies)
                    const hasAmesa = uniqueSectorsSet.has('AMESA') || uniqueSectorsSet.has('amesa');
                    const hasEurope = uniqueSectorsSet.has('EUROPE') || uniqueSectorsSet.has('europe');

                    // 3. If either exists, remove both and add EMEA
                    if (hasAmesa || hasEurope) {
                        uniqueSectorsSet.delete('AMESA');
                        uniqueSectorsSet.delete('amesa');
                        uniqueSectorsSet.delete('EUROPE');
                        uniqueSectorsSet.delete('europe');
                        uniqueSectorsSet.add('EMEA');
                    }

                    // 4. Convert back to array
                    const uniqueSectors = Array.from(uniqueSectorsSet);
                    // --- MODIFICATION END ---

                    const sectorNameList = uniqueSectors.map(sector =>
                        sector === "" ? "Blank" : sector
                    );
                }
            }
        } catch (error) {
            console.error("Error fetching sector options:", error);
        }
    }

    async function fetchChartData(selectedDate: any, isClosedDate: any, timezoneFilter: string, sector: string) {
        try {
            setChartLoading(true);
            const res = await fetchIncidentsSummary(selectedDate, isClosedDate, timezoneFilter, sector);
            if (res.success) {
                const result = transformData(res.data);
                setWidgetData(result);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setChartLoading(false);
        }
    }

    // Function to transform response data into widget-friendly format
    const transformData = (response: any) => {
        const data = response?.data || response || {};
        let widgetData: any[] = [];

        if (data.mim?.length > 0) {
            const item = data.mim[0];
            const change = item.lastMonthCount === 0
                ? (item.currentMonthCount > 0 ? 100 : 0)
                : ((item.currentMonthCount - item.lastMonthCount) / item.lastMonthCount) * 100;
            const chartData = [
                { name: 'MIMs', value: item.mimsCount, formattedName: `${item.mimsCount || 0}` },
                { name: 'Preventive MIMs', value: item.preventiveCount, formattedName: `${item.preventiveCount || 0}` }
            ];

            widgetData.push({
                title: 'MIMs',
                value: item.totalCount,
                data: chartData,
                change: Math.abs(Number(change.toFixed(2))),
                changeType: item.currentMonthCount > item.lastMonthCount ? 'increase' : 'decrease'
            });
        }


        if (data.majorProblemBacklog?.length > 0) {
            const item = data.majorProblemBacklog[0];
            const total = item.rcaCount + item.fixInProgressCount + item.closedCount + item.newCount;

            const backlogData = [
                { name: 'Root Cause Analysis', value: item.rcaCount, formattedName: `${item.rcaCount}` },
                { name: 'Fix In Progress', value: item.fixInProgressCount, formattedName: `${item.fixInProgressCount}` },
                { name: 'Closed', value: item.closedCount, formattedName: `${item.closedCount}` },
                { name: 'New', value: item.newCount, formattedName: `${item.newCount}` }
            ];

            setMajorProblemBacklogData(backlogData.sort((a, b) => b.name.localeCompare(a.name)));
        }
        return widgetData;
    };

    const fetchMimsMetricChartData = async (sector: string, isClosedDate: any, filterMonth: any) => {
        try {
            SetMimsMetricChartLoader(true);
            const res = await fetchMimsMetricTrend(sector, isClosedDate, filterMonth);
            if (res.success) {
                setMimsRawData(res.data);
                const options = buildMimsChartOption(res.data.currentYearIncident, res.data.lastYearIncident, 'all');
                SetMimsMetricChartOptions(options);
            }
        } catch (error) {
            console.error('Error fetching incident trend data:', error);
        } finally {
            SetMimsMetricChartLoader(false);
        }
    };

    const getMonthLabel = (yearMonth: string) => {
        const isYearFilter = filterMonth?.split('-')?.filter(df => !!df)?.length === 1
        return isYearFilter ? filterMonth : dayjs(`${yearMonth}-01`).format("MMM YYYY");
    };

    // --- RENAMED FUNCTION (adds Sector and Month) ---
    const getDynamicTitleWithMonth = (baseTitle: string) => {
        // Check if a specific sector is selected (not 'all' or 'Not Provided')
        const isSectorSelected = sectorName && sectorName !== 'all' && sectorName !== 'Not Provided';

        // Check if a specific month is selected (assuming '0M' means "all time" or no specific month)
        // ** Using initialMonth logic from your other files **
        const isMonthSelected = filterMonth && filterMonth !== getInitialMonth();

        // Get the formatted month label if a month is selected
        const isYearFilter = filterMonth?.split('-')?.filter(df => !!df)?.length === 1
        const monthLabel = isYearFilter ? filterMonth : isMonthSelected ? moment(filterMonth, 'YYYY-MM').format('MMM YYYY') : '';

        const titleParts = [baseTitle];

        // Add sector if selected
        if (isSectorSelected) {
            titleParts.push(sectorName);
        }

        // Add month if selected
        if (isMonthSelected) {
            titleParts.push(monthLabel);
        }

        // Join all parts with a separator
        return titleParts.join(' - ');
    };

    // --- NEW FUNCTION (adds Sector ONLY) ---
    const getDynamicTitleWithoutMonth = (baseTitle: string) => {
        // Check if a specific sector is selected (not 'all' or 'Not Provided')
        const isSectorSelected = sectorName && sectorName !== 'all' && sectorName !== 'Not Provided';

        const titleParts = [baseTitle];

        // Add sector if selected
        if (isSectorSelected) {
            titleParts.push(sectorName);
        }

        // Join all parts with a separator
        return titleParts.join(' - ');
    };

    const buildMimsChartOption = (
        mimsData: any[],
        avgData: any[],
        mimsType: 'all' | 'Actual MIMs' | 'Preventive MIMs'
    ): Record<string, any> => {
        const mims = mimsData.map(item => item.mimsCount);
        const preventiveMims = mimsData.map(item => item.preventiveCount);
        const actualMims = mims.map((val, i) => val - preventiveMims[i]);

        const avgValue = avgData[0]?.avg ?? 0;
        const preventiveAvg = avgData[0]?.preventiveAvg ?? 0;
        const actualAvg = avgValue - preventiveAvg;

        const avgSeries = mimsData.map(() =>
            mimsType === 'Preventive MIMs' ? preventiveAvg :
                mimsType === 'Actual MIMs' ? actualAvg :
                    avgValue
        );

        const dates = mimsData.map(item => getMonthLabel(item.yearMonth));

        const dataSeries: any[] = [];

        // First line: primary series
        if (mimsType === 'all') {
            dataSeries.push({
                name: 'MIMs',
                type: 'line',
                data: mims,
                symbol: 'circle',
                color: '#009BD8',
                lineStyle: { width: 3 },
                label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' },
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0, y: 1, x2: 0, y2: 0,
                        colorStops: [
                            { offset: 0, color: '#053367' },
                            { offset: 1, color: '#009BD8' }
                        ]
                    }
                }
            });
        } else if (mimsType === 'Actual MIMs') {
            dataSeries.push({
                name: 'Actual MIMs',
                type: 'line',
                data: actualMims,
                symbol: 'circle',
                color: '#009BD8',
                lineStyle: { width: 3 },
                label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' },
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0, y: 1, x2: 0, y2: 0,
                        colorStops: [
                            { offset: 0, color: '#053367' },
                            { offset: 1, color: '#009BD8' }
                        ]
                    }
                }
            });
        } else if (mimsType === 'Preventive MIMs') {
            dataSeries.push({
                name: 'Preventive MIMs',
                type: 'line',
                data: preventiveMims,
                symbol: 'circle',
                color: '#9BD800',
                lineStyle: { width: 3 },
                label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' },
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0, y: 1, x2: 0, y2: 0,
                        colorStops: [
                            { offset: 0, color: '#053367' },
                            { offset: 1, color: '#9BD800' }
                        ]
                    }
                }
            });
        }

        // Second line: average series
        dataSeries.push({
            name:
                mimsType === 'Preventive MIMs' ? `${moment(filterMonth).year() - 1} Avg Preventive MIMs` :
                    mimsType === 'Actual MIMs' ? `${moment(filterMonth).year() - 1} Avg Actual MIMs` :
                        `${moment(filterMonth).year() - 1} Avg MIMs`,
            type: 'line',
            data: avgSeries,
            symbol: 'none',
            color:
                mimsType === 'Preventive MIMs' ? '#99C2FF' :
                    mimsType === 'Actual MIMs' ? '#FFC239' :
                        '#BBBBBB',
            lineStyle: { width: 2 }
        });

        const allValues = dataSeries.flatMap(s => s.data);
        const maxVal = Math.max(...allValues);
        const minVal = Math.min(...allValues);
        const yMax = Math.ceil(maxVal / 10) * 10;
        const yMin = Math.max(0, Math.floor(minVal / 10) * 10);

        return {
            backgroundColor: '#003771',
            tooltip: {
                trigger: 'axis',
                textStyle: { color: '#A7ADCB' },
                confine: true,
                backgroundColor: '#003771',
                formatter: function (params: any[]) {
                    const date = params[0]?.axisValueLabel;
                    let tooltipContent = `<div><strong>Date:</strong> ${date}</div>`;
                    params.forEach((item) => {
                        tooltipContent += `<div><strong>${item.seriesName}</strong>: ${item.value}</div>`;
                    });
                    return tooltipContent;
                }
            },
            grid: { top: '7%' },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: dates,
                axisLabel: { color: '#A7ADCB' },
                splitLine: { show: false }
            },
            yAxis: {
                type: 'value',
                min: yMin,
                max: yMax,
                axisLabel: {
                    color: '#A7ADCB',
                    formatter: (value: number) => (value >= 1000 ? `${value / 1000}k` : value)
                },
                splitLine: { show: false }
            },
            series: dataSeries,
            legend: {
                orient: 'horizontal',
                bottom: '1%',
                left: '2%',
                textStyle: { color: '#A7ADCB' },
                selectedMode: false,
                itemWidth: 10,
                itemHeight: 10,
                icon: 'roundRect'
            }
        };
    };

    const buildProblemChartOption = (
        problemData: any[],
        avgData: any[]
    ): Record<string, any> => {
        const problemTickets = problemData.map(item => item.problemTicketsCount);
        const avgValue = avgData[0]?.avg ?? 0;
        const avgSeries = problemData.map(() =>
            avgValue
        );
        const dates = problemData.map(item => getMonthLabel(item.yearMonth));

        const dataSeries: any[] = [];

        // First line: primary series

        dataSeries.push({
            name: 'Problem Tickets',
            type: 'line',
            data: problemTickets,
            symbol: 'circle',
            color: '#009BD8',
            lineStyle: { width: 3 },
            label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' },
            areaStyle: {
                color: {
                    type: 'linear',
                    x: 0, y: 1, x2: 0, y2: 0,
                    colorStops: [
                        { offset: 0, color: '#053367' },
                        { offset: 1, color: '#009BD8' }
                    ]
                }
            }
        });

        // Second line: average series
        dataSeries.push({
            name: `${moment(filterMonth).year() - 1} Avg Problem Tickets`,
            type: 'line',
            data: avgSeries,
            symbol: 'none',
            color: '#BBBBBB',
            lineStyle: { width: 2 }
        });

        const allValues = dataSeries.flatMap(s => s.data);
        const Max = Math.max(...allValues)
        const Min = Math.min(...allValues)

        const yMax = Math.ceil(Max / 10) * 10;
        const yMin = Math.max(0, Math.floor(Min / 10) * 10);

        return {
            backgroundColor: '#003771',
            tooltip: {
                trigger: 'axis',
                textStyle: { color: '#A7ADCB' },
                confine: true,
                backgroundColor: '#003771',
                formatter: function (params: any[]) {
                    const date = params[0]?.axisValueLabel;
                    let tooltipContent = `<div><strong>Date:</strong> ${date}</div>`;
                    params.forEach((item) => {
                        tooltipContent += `<div><strong>${item.seriesName}</strong>: ${item.value}</div>`;
                    });
                    return tooltipContent;
                }
            },
            grid: { top: '7%' },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: dates,
                axisLabel: { color: '#A7ADCB' },
                splitLine: { show: false }
            },
            yAxis: {
                type: 'value',
                min: yMin,
                max: yMax,
                axisLabel: {
                    color: '#A7ADCB',
                    formatter: (value: number) => (value >= 1000 ? `${value / 1000}k` : value)
                },
                splitLine: { show: false }
            },
            series: dataSeries,
            legend: {
                orient: 'horizontal',
                bottom: '1%',
                left: '2%',
                textStyle: { color: '#A7ADCB' },
                selectedMode: false,
                itemWidth: 10,
                itemHeight: 10,
                icon: 'roundRect'
            }
        };
    };

    const fetchProblemMetricChartData = async (sector: string, isClosedDate: any, filterMonth: any) => {
        try {
            SetProblemMetricChartLoader(true);
            const res = await fetchProblemTicketsMetricTrend(sector, isClosedDate, filterMonth);
            if (res.success) {
                const options = buildProblemChartOption(res.data.currentYearIncident, res.data.lastYearIncident);
                SetProblemMetricChartOptions(options);
            }
        } catch (error) {
            console.error('Error fetching problem tickets trend data:', error);
        } finally {
            SetProblemMetricChartLoader(false);
        }
    };

    const handleFilterChange = (option: string) => {
        if (option === 'all' || option === 'Actual MIMs' || option === 'Preventive MIMs') {
            const options = buildMimsChartOption(
                mimsRawData.currentYearIncident,
                mimsRawData.lastYearIncident,
                option
            );
            SetMimsMetricChartOptions(options);
        } else {
            console.warn('Invalid MIMs type selected:', option);
        }
    };

    const handleSectorChange = (value: string) => {
        setSectorName(value);
        localStorage.setItem('xopsMIMReportsSector', value)
    };

    // Function to handle adding a new insight
    const handleAddInsight = async (newInsight: any) => {
        try {
            setLoader(true);
            const response = await addOrUpdateInsights({
                title: newInsight?.title,
                xopsType: 'Sector Metrics',
                description: newInsight?.description
            });
            if (response.success) {
                console.log('response', response);
                fetchInsights(filterMonth);
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Failed to add insight:', error);
        }
    };

    // Function to handle updating an insight
    const handleUpdateInsight = async (updatedInsight: any) => {
        try {
            setLoader(true);
            const response = await addOrUpdateInsights({
                title: updatedInsight?.title,
                xopsType: 'Sector Metrics',
                description: updatedInsight?.description,
                insight_id: updatedInsight?.id
            });
            if (response.success) {
                console.log('response', response);
                fetchInsights(filterMonth);
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Failed to update insight:', error);
        }
    };

    // Function to handle deleting an insight
    const handleDeleteInsight = async (insightId: number) => {
        try {
            setLoader(true);
            const response = await deleteInsights(insightId);
            if (response.success) {
                console.log('response', response);
                fetchInsights(filterMonth);
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Failed to delete insight:', error);
        }
    };

    async function fetchHSCharts(selectedMonth: string, platform: string, technology: string, sector: string) {
        try {
            setIsLoading(true);
            const response = await fetchHappySignalOverview(selectedMonth, platform, technology, sector);

            if (response.success && response.data) {
                const { happinessIndex, averageLostTime, responseRate, uniquePersonas } =
                    response.data;

                // Normalize number widgets
                setHappysignalsData({
                    happinessIndex,
                    averageLostTime,
                    responseRate,
                    uniquePersonas
                });
            }
        } catch (error) {
            console.error('Error while fetching chart data:', error);
        } finally {
            setIsLoading(false);
        }
    }

    async function fetchHSTrendCharts(platform: string, technology: string, sector: string, filterMonth: any) {
        try {
            setIsTrendLoading(true);
            const response = await fetchHappySignalOverviewTrend(platform, technology, sector, '', '', '', filterMonth);

            if (response.success && response.data) {
                const { happinessIndexTrend, responseVolume, happinessIndexLastYearAvg, responseVolumeLastYearAvg } = response.data;

                // Normalize Happiness Index Trend data
                const trendData: { [key: string]: number } = {};
                happinessIndexTrend.forEach((item: any) => {
                    const formattedMonth = moment(item.month, 'MMM YYYY').format('YYYY-MM');
                    trendData[formattedMonth] = item.value;
                });

                setHappinessIndexTrendData(trendData);
                const avg2024TrendData: [string, number][] = Object.keys(trendData).map(
                    (month) => [month, happinessIndexLastYearAvg] as [string, number]
                );

                setLastYearAvgData(avg2024TrendData);


                // Normalize Response Volume Bar Chart
                const formattedResponseVolume = responseVolume.map((item: any) => ({
                    name: item.month,
                    value: item.value
                }));
                setResponseVolumeData(formattedResponseVolume);

                const xAxisLabels = formattedResponseVolume.map((item: any) => item.name);
                const seriesData = formattedResponseVolume.map((item: any) => item.value);

                const responseLastYearAvg = seriesData.map(() => responseVolumeLastYearAvg);
                const allValues = [...seriesData, ...responseLastYearAvg];
                const maxVal = Math.max(...allValues);
                const minVal = Math.min(...allValues);

                const yMax = Math.ceil(maxVal / 10) * 10;
                const yMin = Math.max(0, Math.floor(minVal / 10) * 10);

                const BarChartoptions = {
                    title: { show: false },
                    tooltip: {
                        trigger: 'axis',
                        textStyle: { color: '#A7ADCB' },
                        confine: true,
                        backgroundColor: '#003771',
                        formatter: function (params: any[]) {
                            const date = params[0]?.axisValueLabel;
                            let tooltipContent = `<div><strong>Date:</strong> ${date}</div>`;
                            params.forEach((item) => {
                                tooltipContent += `<div><strong>${item.seriesName}</strong>: ${item.value}</div>`;
                            });
                            return tooltipContent;
                        }
                    },
                    grid: { left: '5%', right: '5%', bottom: '10%', containLabel: true },
                    xAxis: {
                        type: 'category',
                        data: xAxisLabels,
                        axisTick: { show: false },
                        axisLine: { show: true },
                        axisLabel: {
                            color: `${theme.palette.text.subtle}`,
                            rotate: window.innerWidth < 600 ? 30 : 0,
                            formatter: (val: string) => (val.length >= 15 ? val.slice(0, 15) + '…' : val)
                        }
                    },
                    yAxis: {
                        type: 'value',
                        min: yMin,
                        max: yMax,
                        splitLine: { show: false },
                        axisLabel: { color: `${theme.palette.text.subtle}` }
                    },
                    series: [
                        {
                            name: 'Happy Signals Response Volume',
                            type: 'bar',
                            data: seriesData,
                            barWidth: '30%',
                            barMaxWidth: '30%',
                            label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' },
                            // barCategoryGap: '20%',
                            itemStyle: { color: theme.palette.secondary.main }
                        },
                        {
                            name: `${moment(filterMonth).year() - 1} Average`,
                            label: { show: true, position: 'top', fontSize: 12, color: '#00F0F0' },
                            type: 'line',
                            data: responseLastYearAvg,
                            symbol: 'none',
                            color: '#FFC239',
                            lineStyle: { width: 2 }
                        }
                    ],
                    legend: {
                        orient: 'horizontal',
                        bottom: '1%',
                        left: '2%',
                        textStyle: { color: '#A7ADCB' },
                        selectedMode: false,
                        itemWidth: 10,
                        itemHeight: 10,
                        icon: 'roundRect'
                    }
                };

                setResponseVolumeBarChartOptions(BarChartoptions);
            }
        } catch (error) {
            console.error('Error while fetching trend chart data:', error);
        } finally {
            setIsTrendLoading(false);
        }
    }

    useEffect(() => {
        fetchSectorOptionsData()
        fetchInsights(filterMonth);
        fetchHSCharts(filterMonth, '', '', sectorName);
        fetchHSTrendCharts('', '', sectorName, filterMonth);
    }, []);

    useEffect(() => {
        fetchInsights(filterMonth);
        fetchChartData(filterMonth, isClosed, 'UTC', sectorName);
        fetchHSCharts(filterMonth, '', '', sectorName);
        fetchMimsMetricChartData(sectorName, isClosed, filterMonth);
        fetchProblemMetricChartData(sectorName, isClosed, filterMonth);
    }, [filterMonth, isClosed])

    useEffect(() => {
        fetchMimsMetricChartData(sectorName, isClosed, filterMonth);
        fetchProblemMetricChartData(sectorName, isClosed, filterMonth);
        fetchChartData(filterMonth, isClosed, 'UTC', sectorName);
        fetchHSTrendCharts('', '', sectorName, filterMonth);
        fetchHSCharts(filterMonth, '', '', sectorName);
    }, [sectorName, isClosed])

    const handleOpenClosedFilterChange = (event: any) => {
        const isClosed = event.target.checked;
        setIsClosed(isClosed);
    };

    return (
        <PageWrapper>
            <PageHeader title="Sector Metrics" icon={CalenderIcon}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontSize='16px' fontWeight={600} color={theme.palette.textLight.dark}>Metrics by Open Date</Typography>
                    <Switch
                        checked={isClosed}
                        onChange={handleOpenClosedFilterChange}
                        size="small"
                        sx={{
                            width: 44, height: 22, padding: 0,
                            '& .MuiSwitch-switchBase': {
                                '&.Mui-checked': {
                                    transform: 'translateX(22px)',
                                    '& + .MuiSwitch-track': { backgroundColor: '#71758F', opacity: 1, },
                                    '& .MuiSwitch-thumb': { backgroundColor: 'white.main', },
                                },
                            },
                            '& .MuiSwitch-thumb': { width: 14, height: 14, backgroundColor: 'backgroundLight.dark', boxShadow: 'none', },
                            '& .MuiSwitch-track': { borderRadius: 14, backgroundColor: '#71758F', opacity: 1, },
                        }}
                    />
                    <Typography fontSize='16px' fontWeight={600} color={theme.palette.textLight.dark}>Metrics by Closed Date</Typography>
                </Box>
                <SectorFilter
                    sectorName={sectorName}
                    setSectorName={setSectorName}
                    sectorOptionsList={sectorOptionsList}
                    handleChange={handleSectorChange}
                    hasNotProvided={true}
                    boxProps={{
                        sx: { position: 'static', mr: 1 }
                    }}
                />
                {/* <CustomDropdown
                handleChange={(event) => {
                    setChartype(event.target.value)
                    handleChartTypeChange(event.target.value)
                }}
                value={chartType}
                options={chartTypes.map((item: string) => ({ label: item, value: item }))}
            /> */}
                <YearMonthPicker
                    customMonth={customMonth}
                    filterMonth={filterMonth}
                    setCustomMonth={setCustomMonth}
                    setFilterMonth={setFilterMonth}
                    handleChange={() => { }}
                />
                {/* <DateFilterDropdown
                    customMonth={customMonth}
                    filterMonth={filterMonth}
                    setCustomMonth={setCustomMonth}
                    setFilterMonth={setFilterMonth}
                    handleChange={()=>{}}
                    isXOps={true}
                /> */}
            </PageHeader>
            <Grid container spacing={2} marginTop="1rem" px={{ xs: 1, md: 2, lg: 3 }}>
                {widgetData.map((widget, index) => (
                    <Grid item xs={12} md={12} lg={6} key={index}>
                        <PieInfoWidget
                            title={<HighlightedText text={getDynamicTitleWithMonth(widget.title)} />}
                            value={widget.value}
                            change={widget.change}
                            changeType={widget.changeType}
                            isLoading={chartLoading}
                            seriesData={widget.data}
                            customSize="200px"
                            showDollar={false}
                            colorMapping={mimColorMapping}
                            enableSectorCut={false}
                            setEnableSectorCut={false}
                        />
                    </Grid>
                ))}
                <Grid item xs={12} md={12} lg={6} id="major-problem-backlog">
                    <PieInfoWidget
                        title={<HighlightedText text={getDynamicTitleWithMonth("Major Problem Backlog")} />}
                        value={majorProblemBacklogData.reduce((sum, a) => sum + a.value, 0)}
                        change={0}
                        changeType={''}
                        isLoading={chartLoading}
                        seriesData={majorProblemBacklogData}
                        customSize="230px"
                        showDollar={false}
                        colorMapping={majorProblemBacklogColorMapping}
                        enableSectorCut={false}
                        setEnableSectorCut={false}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={1} marginTop="1rem" px={{ xs: 1, md: 2, lg: 3 }}>
                <Grid item xs={12} md={6} lg={6} id="mims">
                    <MultiLineWidget
                        title={<HighlightedText text={getDynamicTitleWithoutMonth("MIMs")} />}
                        isLoading={mimsMetricChartLoader}
                        data={mimsMetricChartOptions}
                        hasFilter={true}
                    >
                        <IncidentFilter
                            sectorName={chartPreference}
                            setSectorName={setChartPreference}
                            sectorOptionsList={['Actual MIMs', 'Preventive MIMs']}
                            handleChange={handleFilterChange}
                            type='MIMs'
                            disabled={false}
                            boxProps={{
                                sx: { minWidth: '20px', position: 'static' }
                            }}
                        />
                    </MultiLineWidget>
                </Grid>
                <Grid item xs={12} md={6} lg={6} id="problem-tickets">
                    <MultiLineWidget
                        title={<HighlightedText text={getDynamicTitleWithoutMonth("Problem Tickets")} />}
                        isLoading={problemMetricChartLoader}
                        data={problemMetricChartOptions}
                        hasFilter={true}
                    >
                    </MultiLineWidget>
                </Grid>
            </Grid>

            <Grid container spacing={1} marginTop="1rem" px={{ xs: 1, md: 2, lg: 3 }}>
                <ITSMOpsTrend metricsType={'Department'} title={getDynamicTitleWithoutMonth('Department Metrics')} chartLabel={'Departments'} sectorName={sectorName} isClosedDate={isClosed} filterMonth={filterMonth} />
                <ITSMOpsTrend metricsType={'Platform'} title={getDynamicTitleWithoutMonth('Platform Ops Metrics')} chartLabel={'platform'} sectorName={sectorName} isClosedDate={isClosed} filterMonth={filterMonth} />
                <ITSMOpsTrend metricsType={'Pipeline'} title={getDynamicTitleWithoutMonth('Pipeline Metrics')} chartLabel={'Technology'} sectorName={sectorName} isClosedDate={isClosed} filterMonth={filterMonth} />
                <ITSMOpsTrend metricsType={'Integration Ops'} title={getDynamicTitleWithoutMonth('Integration Ops Metrics')} chartLabel={'Technology'} sectorName={sectorName} isClosedDate={isClosed} filterMonth={filterMonth} />
                <ITSMOpsTrend metricsType={'Program'} title={getDynamicTitleWithoutMonth('Program Metrics')} chartLabel={'Programs'} sectorName={sectorName} isClosedDate={isClosed} filterMonth={filterMonth} />
                <ITSMOpsTrend metricsType={'BI Ops'} title={getDynamicTitleWithoutMonth('BI Ops Metrics')} chartLabel={'Applications'} sectorName={sectorName} isClosedDate={isClosed} filterMonth={filterMonth} />
                <ITSMOpsTrend metricsType={'AI Ops'} title={getDynamicTitleWithoutMonth('AI Ops Metrics')} chartLabel={'Technology'} sectorName={sectorName} isClosedDate={isClosed} filterMonth={filterMonth} />
                <ITSMOpsTrend metricsType={'ML Ops'} title={getDynamicTitleWithoutMonth('ML Ops Metrics')} chartLabel={'Technology'} sectorName={sectorName} isClosedDate={isClosed} filterMonth={filterMonth} />
            </Grid>
            <Grid container spacing={1} marginTop="1rem" px={{ xs: 1, md: 2, lg: 3 }}>
                <Grid item xs={12} md={6} id="happiness-index">
                    <NumberWidget
                        title={<HighlightedText text={getDynamicTitleWithMonth("Happiness Index")} />}
                        number={happysignalsData.happinessIndex.value < 0 ? happysignalsData.happinessIndex.value : '+' + happysignalsData.happinessIndex.value}
                        change={parseFloat(happysignalsData.happinessIndex.change)}
                        changeType={happysignalsData.happinessIndex.change.startsWith('+') ? 'increase' : 'decrease'}
                        tooltipLabel="Overall employee experience score, ranging from –100 to +100."
                        isHappySignalTooltip={true}
                        isLoading={isLoading}
                        footer={{ footerLink: '#', footerLabel: 'View Details' }}
                        showChangePercentage={true}
                    />
                </Grid>
                <Grid item xs={12} md={6} id="average-lost-time">
                    <NumberWidget
                        title={<HighlightedText text={getDynamicTitleWithMonth("Average Lost Time")} />}
                        number={happysignalsData.averageLostTime.value}
                        change={parseFloat(happysignalsData.averageLostTime.change)}
                        changeType={happysignalsData.averageLostTime.change.startsWith('+') ? 'increase' : 'decrease'}
                        tooltipLabel=""
                        isLoading={isLoading}
                        footer={{ footerLink: '#', footerLabel: 'View Details' }}
                        showChangePercentage={true}
                        reverseTrendColor={true}
                    />
                </Grid>
                <Grid item xs={12} md={6} id="happiness-index-trend">
                    <ChartWidget
                        title={<HighlightedText text={getDynamicTitleWithoutMonth("Happiness Index Trend")} />}
                        data={happinessIndexTrendData}
                        isLoading={isTrendLoading}
                        isFinops={true}
                        isHappySignal={true}
                        lastYearAvgData={lastYearAvgData}
                        tooltipLabel="Overall employee experience score, ranging from –100 to +100."
                    />
                </Grid>
                <Grid item xs={12} md={6} id="response-volume">
                    <CustomBarChartWidget
                        title={<HighlightedText text={getDynamicTitleWithoutMonth("Happy Signals Response Volume")} />}
                        isLoading={isTrendLoading}
                        data={responseVolumeData}
                        options={responseVolumeBarChartOptions}
                        isHappySignal={true}
                    />
                </Grid>
            </Grid>

            <Box marginTop={4} marginBottom={5} px={{ xs: 1, md: 2, lg: 3 }} id="insights">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subHeader" sx={{ color: '#009BDB' }}>{<HighlightedText text={getDynamicTitleWithMonth("Insights/Actions")} />}</Typography>
                    <Divider sx={{ flexGrow: 1, backgroundColor: '#009BDB', marginLeft: 2 }} />
                </Box>
                <Typography
                    variant="bodyText"
                    sx={{ fontStyle: 'italic', color: '#71758F' }}
                >
                    You can display and update Insights/Actions if any.
                </Typography>
            </Box>
            {loader ? (
                <LoaderComponent />
            ) : (
                <Box sx={{ mb: 3, px: { xs: 1, md: 2, lg: 3 } }}> {/* Added padding to match the Box above */}
                    <Insights
                        insights={insights}
                        setInsights={setInsights}
                        onAdd={handleAddInsight}
                        onUpdate={handleUpdateInsight}
                        onDelete={handleDeleteInsight}
                    />
                </Box>
            )}
        </PageWrapper>
    );
};
export default SectorMetrics;